import { createMutation, createTriggerResource } from '@magicdoorinc/ui';
import { createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { WorkOrdersRepository } from '~/repositories/workOrderRepository';
import type { HydrateFile } from '@magicdoorinc/ui';
import type { WorkOrdersFilter } from '~/repositories/workOrderRepository';
import type {
  AddWorkSessionDto,
  CreateWorkOrderDraftBillRequestDto,
  CreateWorkOrderRequestDto,
  SendWorkOrderVendorNotificationDto,
  UpdateWorkOrderLeaseDto,
  UpdateWorkOrderMaintenanceRequestDto,
  UpdateWorkOrderRequestDto,
  UpdateWorkOrderStatusDto,
  UpdateWorkOrderVendorDto,
  UpdateWorkSessionDto,
} from '~/swagger/maintenance/Api';

const repository = new WorkOrdersRepository();

export const [WorkOrdersProvider, useWorkOrders] = createMagicDoorContext('WorkOrders', () => {
  const [filtered, setFilter, filteredActions] = createTriggerResource((filter: WorkOrdersFilter) => repository.getWorkOrders(filter));

  const [newFiles, setAttachedFiles] = createSignal<HydrateFile[]>([]);
  const [workOrder, getWorkOrder, workOrderActions] = createTriggerResource(repository.getWorkOrder.bind(repository));

  const [userWorkOrder, getUserWorkOrder] = createTriggerResource(repository.getUserWorkOrders.bind(repository));

  const addWorkOrder = createMutation(async (payload: CreateWorkOrderRequestDto) => {
    const response = await repository.addWorkOrder(payload);
    workOrderActions.mutate(response);
    return response;
  });

  const updateMaintenanceRequest = createMutation(async (workOrderId: string, payload: UpdateWorkOrderMaintenanceRequestDto) => {
    const response = await repository.updateMaintenanceRequest(workOrderId, payload);
    workOrderActions.mutate(response);
  });

  const deleteWorkOrder = createMutation(async (workOrderId: string) => {
    await repository.deleteWorkOrder(workOrderId);
    filteredActions.refetch();
  });

  const updateWorkOrder = createMutation(async (workOrderId: string, payload: UpdateWorkOrderRequestDto) => {
    const response = await repository.updateWorkOrder(workOrderId, payload);
    workOrderActions.mutate(response);
  });

  const updateWorkOrderStatus = createMutation(async (workOrderId: string, payload: UpdateWorkOrderStatusDto) => {
    const hydrated = await repository.updateWorkOrderStatus(workOrderId, payload);
    filteredActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          items: prev.items.map((i) => ({
            ...i,
            status: i.id === workOrderId ? hydrated.status : i.status,
          })),
        }
    );
  });

  const updateVendor = createMutation(async (workOrderId: string, payload: UpdateWorkOrderVendorDto) => {
    const response = await repository.updateVendor(workOrderId, payload);
    workOrderActions.mutate(response);
    filteredActions.refetch();
  });

  const updateLease = createMutation(async (workOrderId: string, payload: UpdateWorkOrderLeaseDto) => {
    const response = await repository.updateLease(workOrderId, payload);
    workOrderActions.mutate(response);
    filteredActions.refetch();
  });

  const addDraftBill = createMutation(async (workOrderId: string, payload: CreateWorkOrderDraftBillRequestDto) => {
    const hydrated = await repository.addDraftBill(workOrderId, payload);
    workOrderActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          bills: prev.bills ? [...prev.bills, hydrated] : [hydrated],
        }
    );
    return hydrated;
  });
  const updateDraftBill = createMutation(async (workOrderId: string, billId: string, payload: CreateWorkOrderDraftBillRequestDto) => {
    const hydrated = await repository.updateDraftBill(workOrderId, billId, payload);
    workOrderActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          bills: prev.bills?.map((item) => ({
            ...(item.id === billId ? hydrated : item),
          })),
        }
    );
  });

  const deleteDraftBill = createMutation(async (workOrderId: string, billId: string) => {
    await repository.deleteDraftBill(workOrderId, billId);
    workOrderActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          bills: prev.bills?.filter((bill) => bill.id !== billId),
        }
    );
  });

  const addSession = createMutation(async (workOrderId: string, payload: AddWorkSessionDto) => {
    const hydrated = await repository.addWorkOrderSession(workOrderId, payload);
    workOrderActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          sessions: hydrated.sessions,
        }
    );
    return hydrated;
  });

  const updateSession = createMutation(async (workOrderId: string, sessionId: string, payload: UpdateWorkSessionDto) => {
    const hydrated = await repository.updateWorkOrderSession(workOrderId, sessionId, payload);
    workOrderActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          sessions: hydrated.sessions,
        }
    );
    return hydrated;
  });

  const deleteSession = createMutation(async (workOrderId: string, sessionId: string) => {
    const hydrated = await repository.deleteWorkOrderSession(workOrderId, sessionId);
    workOrderActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          sessions: hydrated.sessions,
        }
    );
    return hydrated;
  });

  const convertDraftBill = createMutation(async (workOrderId: string, billId: string) => {
    await repository.convertDraftBill(workOrderId, billId);
    workOrderActions.refetch();
  });

  const addWorkOrderBillFiles = createMutation(
    async (workOrderId: string, billId: string, files: { file: File; description?: string }[]) => {
      const sessionIds = await repository.uploadDraftBillFiles(
        workOrderId,
        billId,
        files.map((f) => f.file)
      );
      const hydrated = await repository.addDraftBillFiles(
        workOrderId,
        billId,
        sessionIds.map((id, i) => ({
          uploadSessionId: id,
          description: files[i].description,
        }))
      );
      workOrderActions.mutate(
        (prev) =>
          prev && {
            ...prev,
            bills: prev.bills?.map((item) => (item.id === billId ? hydrated : item)),
          }
      );
    }
  );

  const deleteBillFile = createMutation(async (workOrderId: string, billId: string, fileId: string) => {
    await repository.deleteBillFile(workOrderId, billId, fileId);
    workOrderActions.mutate(
      (prev) =>
        prev && {
          ...prev,
          bills: prev.bills?.map((item) => ({
            ...item,
            files: item.files?.filter((file) => file.id !== fileId),
          })),
        }
    );
  });

  const createVendorNotification = createMutation(async (workOrderId: string, payload: SendWorkOrderVendorNotificationDto) => {
    if (!workOrderId) return;
    await repository.createWorkOrderNotification(workOrderId, payload);
    workOrderActions.refetch();
  });

  return {
    filtered: filtered,
    setFilter: (filter: WorkOrdersFilter) => setFilter(filter),
    addWorkOrder,
    updateMaintenanceRequest,
    getWorkOrder,
    workOrder,
    createVendorNotification,
    deleteWorkOrder,
    updateWorkOrder,
    updateWorkOrderStatus,
    workOrderActions,
    updateVendor,
    updateLease,
    addDraftBill,
    updateDraftBill,
    deleteDraftBill,
    convertDraftBill,
    addSession,
    updateSession,
    deleteSession,
    addWorkOrderBillFiles,
    deleteBillFile,
    userWorkOrder,
    getUserWorkOrder,
    newFiles,
    setAttachedFiles,
    refetchWorkOrders: workOrderActions.refetch,
  };
});
